import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConfirmSettingsContactChangeRequest,
  RequestSettingsEmailChangeRequest,
  RequestSettingsPhoneChangeRequest,
  UpdateSettingsBusinessRequest,
  UpdateSettingsNotificationsRequest,
  UpdateSettingsPreferencesRequest,
  UpdateSettingsProfileRequest,
  UpdateSettingsStoreRequest,
  UpdateSettingsThemeRequest,
} from '@invento/shared';
import {
  settingsService,
  type UploadSettingsBrandingLogoInput,
  type UploadSettingsProfilePhotoInput,
  type SettingsChangePasswordRequest,
} from '../../api/settings.service';
import {
  SESSION_SURFACE_REFETCH_INTERVAL_MS,
  sessionSurfaceQueryKeys,
} from '../../../auth/session-surface-query-keys';

export const settingsQueryKeys = {
  all: ['settings'] as const,
  theme: () => [...settingsQueryKeys.all, 'theme'] as const,
  brandingLogo: () => [...settingsQueryKeys.theme(), 'branding-logo'] as const,
  account: () => [...settingsQueryKeys.all, 'account'] as const,
  profilePhoto: () => [...settingsQueryKeys.account(), 'profile-photo'] as const,
  business: () => [...settingsQueryKeys.all, 'business'] as const,
  securitySessions: () => sessionSurfaceQueryKeys.account,
  notifications: () => [...settingsQueryKeys.all, 'notifications'] as const,
  preferences: () => [...settingsQueryKeys.all, 'preferences'] as const,
  stores: () => [...settingsQueryKeys.all, 'stores'] as const,
};

export const useSettingsTheme = () =>
  useQuery({
    queryKey: settingsQueryKeys.theme(),
    queryFn: settingsService.getTheme,
  });

export const useSettingsBrandingLogo = (enabled: boolean) =>
  useQuery({
    queryKey: settingsQueryKeys.brandingLogo(),
    queryFn: settingsService.getBrandingLogo,
    enabled,
    staleTime: 0,
  });

export const useSettingsAccount = (enabled = true) =>
  useQuery({
    queryKey: settingsQueryKeys.account(),
    queryFn: settingsService.getAccount,
    enabled,
  });

export const useVerifySettingsPosPin = () => useMutation({
  mutationFn: (input: { pin: string }) => settingsService.verifyPosPin(input),
});

export const useSetSettingsPosPin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: settingsService.setPosPin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.account() });
    },
  });
};

export const useSettingsProfilePhoto = (enabled: boolean) =>
  useQuery({
    queryKey: settingsQueryKeys.profilePhoto(),
    queryFn: settingsService.getProfilePhoto,
    enabled,
    staleTime: 0,
  });

export const useSettingsBusiness = (enabled: boolean) =>
  useQuery({
    queryKey: settingsQueryKeys.business(),
    queryFn: settingsService.getBusiness,
    enabled,
  });

export const useUpdateSettingsBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSettingsBusinessRequest) => settingsService.updateBusiness(input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.business(), response);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.business() });
    },
  });
};

export const useSettingsSecuritySessions = () =>
  useQuery({
    queryKey: settingsQueryKeys.securitySessions(),
    queryFn: settingsService.listSecuritySessions,
    refetchInterval: SESSION_SURFACE_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

export const useRevokeSettingsSecuritySession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => settingsService.revokeSecuritySession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.securitySessions() });
      queryClient.invalidateQueries({ queryKey: sessionSurfaceQueryKeys.team });
    },
  });
};

export const useRevokeOtherSettingsSecuritySessions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: settingsService.revokeOtherSecuritySessions,
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.securitySessions(), {
        success: response.success,
        data: {
          sessions: response.data.sessions,
          currentSessionKnown: response.data.currentSessionKnown,
        },
      });
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.securitySessions() });
      queryClient.invalidateQueries({ queryKey: sessionSurfaceQueryKeys.team });
    },
  });
};

export const useSettingsNotifications = () =>
  useQuery({
    queryKey: settingsQueryKeys.notifications(),
    queryFn: settingsService.getNotifications,
  });

export const useSettingsPreferences = () =>
  useQuery({
    queryKey: settingsQueryKeys.preferences(),
    queryFn: settingsService.getPreferences,
  });

export const useSettingsStores = (enabled: boolean) =>
  useQuery({
    queryKey: settingsQueryKeys.stores(),
    queryFn: settingsService.getStores,
    enabled,
  });

export const useUpdateSettingsStore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ storeId, input }: { storeId: string; input: UpdateSettingsStoreRequest }) =>
      settingsService.updateStore(storeId, input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.stores(), (current: unknown) => {
        if (!current || typeof current !== 'object' || !('data' in current)) return current;
        const envelope = current as Awaited<ReturnType<typeof settingsService.getStores>>;
        return {
          ...envelope,
          data: {
            ...envelope.data,
            stores: envelope.data.stores.map((store) =>
              store.storeId === response.data.storeId ? response.data : store,
            ),
          },
        };
      });
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.stores() });
    },
  });
};

export const useUpdateSettingsPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSettingsPreferencesRequest) =>
      settingsService.updatePreferences(input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.preferences(), response);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.preferences() });
    },
  });
};

export const useUpdateSettingsNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSettingsNotificationsRequest) =>
      settingsService.updateNotifications(input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.notifications(), response);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.notifications() });
    },
  });
};

export const useUpdateSettingsProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSettingsProfileRequest) => settingsService.updateProfile(input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.account(), response);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.account() });
    },
  });
};

export const useRequestSettingsEmailChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RequestSettingsEmailChangeRequest) =>
      settingsService.requestEmailChange(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.account() });
    },
  });
};

export const useConfirmSettingsEmailChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ConfirmSettingsContactChangeRequest) =>
      settingsService.confirmEmailChange(input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.account(), {
        success: response.success,
        data: response.data.account,
      });
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.account() });
    },
  });
};

export const useRequestSettingsPhoneChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RequestSettingsPhoneChangeRequest) =>
      settingsService.requestPhoneChange(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.account() });
    },
  });
};

export const useConfirmSettingsPhoneChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ConfirmSettingsContactChangeRequest) =>
      settingsService.confirmPhoneChange(input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.account(), {
        success: response.success,
        data: response.data.account,
      });
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.account() });
    },
  });
};

export const useUploadSettingsProfilePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UploadSettingsProfilePhotoInput) =>
      settingsService.uploadProfilePhoto(input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.account(), {
        success: response.success,
        data: response.data.account,
      });
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.account() });
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.profilePhoto() });
    },
  });
};

export const useDeleteSettingsProfilePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: settingsService.deleteProfilePhoto,
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.account(), {
        success: response.success,
        data: response.data.account,
      });
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.account() });
      queryClient.removeQueries({ queryKey: settingsQueryKeys.profilePhoto() });
    },
  });
};

export const useUploadSettingsBrandingLogo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UploadSettingsBrandingLogoInput) =>
      settingsService.uploadBrandingLogo(input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.theme(), response);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.theme() });
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.brandingLogo() });
    },
  });
};

export const useDeleteSettingsBrandingLogo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: settingsService.deleteBrandingLogo,
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.theme(), response);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.theme() });
      queryClient.removeQueries({ queryKey: settingsQueryKeys.brandingLogo() });
    },
  });
};

export const useChangeSettingsPassword = () =>
  useMutation({
    mutationFn: (input: SettingsChangePasswordRequest) => settingsService.changePassword(input),
  });

export const useUpdateSettingsTheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSettingsThemeRequest) => settingsService.updateTheme(input),
    onSuccess: (response) => {
      queryClient.setQueryData(settingsQueryKeys.theme(), response);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.theme() });
    },
  });
};
